import { createSelector } from '@reduxjs/toolkit'
import { createStructuredSelector, Selector } from 'reselect'

import {
  experimentFlagMap,
  BillingExperiment,
  ExperimentGroup,
} from 'common/billing/constants/BillingExperiment'
import {
  getAutomationProductTier,
  getBillableContacts,
} from 'common/billing/entities/product/selectors'
import { getTierLimitState } from 'common/billing/helpers/tierLimit'
import { UpgradeSource } from 'common/billing/interfaces'
import { UpgradeRequest } from 'common/billing/interfaces/upgradeRequest'
import {
  getAccountFlags,
  getBillingAddressFlags,
  getInAppCardAttachmentFlags,
  getIsPro,
  isTrialAvailable,
} from 'common/core/selectors/appSelectors'

/**
 * Local usage from upgradeRequest selectors to handle cycle deps.
 */
const getUpgradeRequest = (state: RootState): UpgradeRequest => state.billing.upgradeRequest
const getUpgradeRequestSource = createSelector([getUpgradeRequest], (request) => request.source)

/**
 * Checks if the account is already participating in any experiment.
 *
 * This function iterates over all possible experiment flags and checks if any of them
 * are present in the current account's flags.
 *
 * @param {RootState} state - The current state of the application.
 * @returns {boolean} - `true` if the account is in any experiment, otherwise `false`.
 */
export const getIsAlreadyInAnyExperiment = (state: RootState): boolean => {
  const flags = getAccountFlags(state)
  const allExperimentFlags = Object.values(experimentFlagMap).flat()

  return allExperimentFlags.some((flag) => Boolean(flags[flag]))
}
/**
 * Checks if an experiment already exists for the given state and experiment.
 *
 * This function determines if any of the flags associated with the given experiment
 * are present in the current account's flags. Additionally, it checks if the trial
 * is available.
 */
export const getIsAlreadyInExperiment = (
  state: RootState,
  experiment: BillingExperiment,
): boolean => {
  const flags = getAccountFlags(state)
  const experimentFlags = experimentFlagMap[experiment]
  return experimentFlags.some((flag) => Boolean(flags[flag]))
}
/**
 * Add here any conditions that have to be met for given experiment
 * in format of state selector
 */
export const getExperimentConditionsMet: Selector<
  RootState,
  Record<BillingExperiment, boolean>
> = createStructuredSelector({
  [BillingExperiment.UPGRADE_PAGE]: (state) =>
    isTrialAvailable(state) && getIsAlreadyInExperiment(state, BillingExperiment.UPGRADE_PAGE),
  [BillingExperiment.TIER_NOTIFICATION]: getIsPro,
})
export const getActiveExperiment: Selector<RootState, BillingExperiment | null> = (
  state: RootState,
) => {
  const experiment = getUpgradeRequest(state).experiment
  if (!experiment) {
    return null
  }

  if (!getExperimentConditionsMet(state)[experiment]) {
    return null
  }

  return experiment
}
export const getActiveAGroupExperiment: Selector<RootState, BillingExperiment | null> =
  createSelector([getActiveExperiment, getAccountFlags], (experiment, flags) => {
    if (!experiment) {
      return null
    }

    const [experimentFlag] = experimentFlagMap[experiment] || []
    if (!flags?.[experimentFlag]) {
      return null
    }

    return experiment
  })
export const getCheckoutFlowExperimentSource = createSelector(
  [getUpgradeRequestSource, getInAppCardAttachmentFlags, getBillingAddressFlags],
  (source, inAppCardAttachmentExperiment, billingAddressExperiment) => {
    let experimentSource

    if (inAppCardAttachmentExperiment.isEnabled) {
      experimentSource = `${UpgradeSource.IN_APP_CARD_ATTACHMENT_EXPERIMENT}.${source}`
    }
    if (billingAddressExperiment.isEnabled) {
      experimentSource = `${UpgradeSource.BILLING_ADDRESS_ONLY_US_EXPERIMENT}.${source}`
    }

    return experimentSource
  },
)
/**
 * Method, that received billing experiment and return the group, that an account belongs to
 * If account doesn't have experiment, method returns undefined
 */
export const getExperimentGroup = createSelector(
  [
    getAccountFlags,
    (state: RootState, experiment: BillingExperiment) => experiment,
    (state: RootState, experiment: BillingExperiment) =>
      getExperimentConditionsMet(state, experiment)[experiment],
  ],
  (accountFlags, experiment, condition): ExperimentGroup | undefined => {
    if (!experiment || !experimentFlagMap[experiment] || !condition) {
      return undefined
    }

    const [groupA, groupB] = experimentFlagMap[experiment]
    if (accountFlags[groupA]) {
      return ExperimentGroup.A
    }

    if (accountFlags[groupB]) {
      return ExperimentGroup.B
    }

    return undefined
  },
)

export const getActiveExperimentHash: Selector<RootState, string | undefined> = createSelector(
  [getActiveExperiment, getAccountFlags],
  (experiment, flags) => {
    if (!experiment) {
      return undefined
    }

    const [flagA, flagB] = experimentFlagMap[experiment] || []
    if (flags?.[flagA]) {
      return `${experiment}_a`
    }
    if (flags?.[flagB]) {
      return `${experiment}_b`
    }

    return undefined
  },
)

/**
 * Selector to get the tier limit notification state based on the automation product tier and current contacts count.
 */
export const getShouldShowTierLimitNotification = createSelector(
  [getAutomationProductTier, getBillableContacts],
  (plan_subscribers, current_contacts_count) => {
    return getTierLimitState(plan_subscribers, current_contacts_count)
  },
)

/**
 * Selector to determine if the tier limit notification should be shown, considering experiments.
 *
 * This selector combines the results of multiple selectors and additional logic to determine
 * if the tier limit notification should be displayed. It takes into account the experiment group,
 * whether the user is already in any experiment, and if the user is already in the tier notification experiment.
 *
 * @param {RootState} state - The current state of the application.
 * @returns {boolean} - `true` if the tier limit notification should be shown, otherwise `false`.
 */
export const getShouldShowTierLimitNotificationWithExperiment = createSelector(
  [getExperimentGroup, getShouldShowTierLimitNotification],
  (group, shouldShowByLimit) => {
    return group === ExperimentGroup.A && shouldShowByLimit
  },
)
